import { WarningIcon } from '@phosphor-icons/react';

import { OverflowTooltip } from '@/components/common/overflow-tooltip';
import { Timeline, type TimelineItem, type TimelineStatus } from '@/components/common/timeline';
import { UserAvatar } from '@/components/common/user-avatar';
import { Drawer } from '@/components/feedback/drawer';
import { customerAvatar } from '@/lib/avatar';

import {
  formatDate,
  formatPaymentMethod,
  formatTimelineDateTime,
  PAYMENT_FOR_LABEL,
} from '../format';
import { usePaymentTimeline } from '../hooks';
import type { PaymentStatus, PaymentTimelineEvent } from '../types';

import { PaymentStatusChip } from './payment-status-chip';
import styles from './payment-timeline-drawer.module.scss';

/** Timeline dot tone per payment status (final positive → success, failed/capture → error,
 *  released → neutral info, temporary states → pending). */
const STATUS_TONE: Record<PaymentStatus, TimelineStatus> = {
  pending: 'pending',
  on_hold: 'pending',
  refund_initiated: 'pending',
  success: 'success',
  debited: 'success',
  refunded: 'success',
  failed: 'error',
  capture_failed: 'error',
  disputed: 'error',
  released: 'info',
};

const toItem = (e: PaymentTimelineEvent): TimelineItem => ({
  status: STATUS_TONE[e.status],
  title: (
    <span className={styles.event}>
      <PaymentStatusChip status={e.status} />
      <span className={styles.eventMethod}>{formatPaymentMethod(e.paymentMethod)}</span>
    </span>
  ),
  timestamp: formatTimelineDateTime(e.timestamp),
});

export type PaymentTimelineDrawerProps = {
  /** The payment-entry id whose timeline to show; null when no row is selected. */
  paymentId: string | null;
  open: boolean;
  onClose: () => void;
};

export function PaymentTimelineDrawer({ paymentId, open, onClose }: PaymentTimelineDrawerProps) {
  const { data } = usePaymentTimeline(open ? paymentId : null);
  const reason = data?.events.find((e) => e.failureReason)?.failureReason;

  return (
    <Drawer open={open} onClose={onClose} title="Payment Timeline" hideFooter>
      {data ? (
        <>
          <div className={styles.header}>
            <div className={styles.payment}>
              {/* A Stripe id is long; it truncates rather than crowding the customer column off the
                  card, with the full value on hover (Figma `30:471647`, ADO 120966). */}
              <OverflowTooltip title={data.header.transactionId}>
                <div className={styles.txn}>{data.header.transactionId}</div>
              </OverflowTooltip>
              <div className={styles.subtitle}>
                Payment for {PAYMENT_FOR_LABEL[data.header.paymentFor]}
              </div>
            </div>
            <div className={styles.customer}>
              <div className={styles.customerInfo}>
                <UserAvatar
                  name={data.header.customerName}
                  src={data.header.customerAvatarUrl ?? customerAvatar(data.header.customerName)}
                  size={24}
                />
                <OverflowTooltip title={data.header.customerName}>
                  <div className={styles.customerName}>{data.header.customerName}</div>
                </OverflowTooltip>
              </div>
              <div className={styles.orderNumber}>
                Order Date - {data.header.orderDate ? formatDate(data.header.orderDate) : '—'}
              </div>
            </div>
          </div>

          <Timeline items={data.events.map(toItem)} />

          {reason ? (
            <div className={styles.banner}>
              <WarningIcon size={16} />
              <span>{reason}</span>
            </div>
          ) : null}
        </>
      ) : null}
    </Drawer>
  );
}
